import { Typography, Input, Form, Radio, Select, Space, Checkbox } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { t } from 'ttag';

import { DatePicker as AntDatePicker } from 'common/components/DatePicker';
import Label from 'common/components/Label';

import { list as listLanguages } from '@domains/Event/language-service';
import { LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT } from '@domains/Organization/service';

const { RangePicker: AntRangePicker } = AntDatePicker;

const LOCALE_DATE = 'L';

export const OTHER_VALUE = 'other';

export const getEventTypeOptions = () => [
  { label: t`Award Ceremony`, value: 'Award Ceremony' },
  { label: t`Conference`, value: 'Conference' },
  { label: t`Consumer Show`, value: 'Consumer Show' },
  { label: t`Expo/Tradeshow`, value: 'Expo/Tradeshow' },
  { label: t`Festival`, value: 'Festival' },
  { label: t`Forum`, value: 'Forum' },
  { label: t`Gala/Fundraiser`, value: 'Gala/Fundraiser' },
  { label: t`Incentive Trip`, value: 'Incentive Trip' },
  { label: t`Internal Training`, value: 'Internal Training' },
  { label: t`Meeting`, value: 'Meeting' },
  { label: t`Networking Event`, value: 'Networking Event' },
  { label: t`Product Launch`, value: 'Product Launch' },
  { label: t`Retreat`, value: 'Retreat' },
  { label: t`Roadshow`, value: 'Roadshow' },
  { label: t`Sales Kick Off`, value: 'Sales Kick Off' },
  { label: t`Seminar`, value: 'Seminar' },
  { label: t`Summit`, value: 'Summit' },
  { label: t`Symposium`, value: 'Symposium' },
  { label: t`Test`, value: 'Test' },
  { label: t`Tournament`, value: 'Tournament' },
  { label: t`Town Hall`, value: 'Town Hall' },
  { label: t`User/Partner Conference`, value: 'User/Partner Conference' },
  { label: t`Webinar`, value: 'Webinar' },
  { label: t`Workshop`, value: 'Workshop' },
  { label: t`Other`, value: OTHER_VALUE },
];

export const findMatchingEventTypeOption = value =>
  getEventTypeOptions().find(
    option =>
      option.value !== OTHER_VALUE && option.value.toLowerCase() === value.toLowerCase().trim(),
  );

const RangePicker = styled(AntRangePicker)`
  width: 100%;
`;

// over riding styles for range picker popup for this component
const PickerPopupStyles = createGlobalStyle`
.ant-picker {
  .ant-picker-input > input {
    text-align: center;
 }

 .ant-calendar-range-middle {
   padding: 0;
 }
}
`;

const { Item } = Form;

const Details = ({
  currentMode,
  name,
  dates,
  code,
  eventFormat,
  eventType,
  credit,
  isReserved,
  primaryLanguage,
  secondaryLanguage,
  secondaryLanguageEnabled,
  setName,
  setDates,
  setCode,
  setEventFormat,
  setEventType,
  setPrimaryLanguage,
  setSecondaryLanguage,
  setSecondaryLanguageEnabled,
}) => {
  const maxDuration = credit?.event_max_duration_days;

  const [showOtherEventType, setShowOtherEventType] = useState(false);

  // Options carry the language code (e.g. 'en', 'zh-Hant') — the format the
  // redeem-event-credit endpoint accepts.
  const languageOptions = useMemo(
    () => listLanguages().map(language => ({ label: language.label, value: language.code })),
    [],
  );

  const sameLanguageError =
    secondaryLanguageEnabled && !!secondaryLanguage && secondaryLanguage === primaryLanguage;

  const onEventTypeChange = value => {
    if (value === OTHER_VALUE) {
      setShowOtherEventType(true);
      setEventType('');
      return;
    }

    const match = findMatchingEventTypeOption(value);

    if (match) {
      setEventType(match.value);
      setShowOtherEventType(false);
    } else {
      setEventType(value);
    }
  };

  const onSecondaryLanguageToggle = e => {
    const { checked } = e.target;
    setSecondaryLanguageEnabled(checked);
    // Clear any selection when the secondary language is turned off so it is not submitted.
    if (!checked) {
      setSecondaryLanguage('');
    }
  };

  return (
    // This form is used just for layout purposes
    <Form layout="vertical" initialValues={{ eventCode: code }}>
      {/* For RangePicker */}
      <PickerPopupStyles />

      {currentMode === LICENSE_TYPE_USER_CREDIT && (
        <Typography.Title level={5}>{t`Event Details`}</Typography.Title>
      )}

      <Item label={<Label isRequired>{t`Event Name`}</Label>}>
        <Input data-testid="name" value={name} onChange={e => setName(e.target.value)} />
      </Item>
      <Item label={<Label isRequired>{t`Event Start and End Date`}</Label>}>
        <RangePicker
          onChange={e => setDates(e)}
          value={dates}
          showTime={false}
          onCalendarChange={d => {
            setDates(d);
          }}
          disabledDate={current => {
            if (credit?.expires_at && current.isAfter(credit?.expires_at, 'days')) {
              return true;
            }

            if (current.isBefore(moment(), 'days')) {
              return true;
            }

            if (!dates || dates.length === 0 || !maxDuration) {
              return false;
            }
            const tooLate = dates[0] && current.diff(dates[0], 'days') > maxDuration;
            const tooEarly = dates[1] && dates[1].diff(current, 'days') > maxDuration;
            return tooEarly || tooLate;
          }}
          format={LOCALE_DATE}
          separator="→"
        />
      </Item>
      <Item
        label={
          <Label
            isRequired
            info={t`This is used to create a unique URL for your Event App, Registration Site and Live Display. Once set, this can only be changed by EventMobi Support.`}
          >
            {t`Event Code`}
          </Label>
        }
        name="eventCode"
        help={isReserved ? t`Event Code can not be one of reserved words` : undefined}
        validateStatus={isReserved ? 'error' : undefined}
        validateTrigger={['onBlur']}
        rules={[
          { required: true, message: t`Event Code is required` },
          { max: 128, message: t`Event Code should consist of maximum 128 characters` },
          { min: 3, message: t`Event Code should consist of at least 3 characters` },
          {
            pattern: /^[a-zA-Z-_\d]+$/,
            message: t`Event Code should not have any special characters, allowed characters include alphabets, numbers, underscores and dashes`,
          },
          {
            pattern: /\D/,
            message: t`Event Code should not consist of only numbers`,
          },
        ]}
      >
        <Input
          data-testid="code"
          prefix="https://www.eventmobi.com/"
          value={code}
          onChange={e => setCode(e.target.value)}
        />
      </Item>
      <Item label={<Label isRequired>{t`Event Format`}</Label>}>
        <Radio.Group
          options={[
            { label: t`In-Person`, value: 'onsite' },
            { label: t`Virtual`, value: 'virtual' },
            { label: t`Hybrid`, value: 'hybrid' },
          ]}
          optionType="button"
          value={eventFormat}
          onChange={e => setEventFormat(e.target.value)}
        />
      </Item>
      <Item label={<Label isRequired>{t`Event Type`}</Label>}>
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Select
            placeholder={t`Select`}
            options={getEventTypeOptions()}
            value={showOtherEventType ? 'other' : eventType || undefined}
            onChange={onEventTypeChange}
          />
          {showOtherEventType && (
            <Input
              data-testid="event-type-other"
              defaultValue={eventType}
              onBlur={e => onEventTypeChange(e.target.value)}
              maxLength={50 /* Flux limits to 50 characters */}
              placeholder={t`Please specify the event type`}
            />
          )}
        </Space>
      </Item>
      <Item label={<Label isRequired>{t`Primary Event Language`}</Label>}>
        <Select
          showSearch
          data-testid="primary-language"
          placeholder={t`Please select a language`}
          optionFilterProp="label"
          options={languageOptions}
          value={primaryLanguage || undefined}
          onChange={value => setPrimaryLanguage(value)}
        />
      </Item>
      <Item>
        <Checkbox checked={secondaryLanguageEnabled} onChange={onSecondaryLanguageToggle}>
          {t`Add another language`}
        </Checkbox>
        <br />
        <Typography.Text type="secondary">
          {t`Provides attendees the option to change the language on the Event App. All of your event content will need to be written in both languages.`}
        </Typography.Text>
      </Item>
      {secondaryLanguageEnabled && (
        <Item
          label={<Label isRequired>{t`Secondary Event Language`}</Label>}
          validateStatus={sameLanguageError ? 'error' : undefined}
          help={
            sameLanguageError
              ? t`The primary and secondary event languages must be different.`
              : undefined
          }
        >
          <Select
            showSearch
            data-testid="secondary-language"
            placeholder={t`Please select a language`}
            optionFilterProp="label"
            options={languageOptions}
            value={secondaryLanguage || undefined}
            onChange={value => setSecondaryLanguage(value)}
          />
        </Item>
      )}
    </Form>
  );
};

Details.propTypes = {
  currentMode: PropTypes.oneOf([LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT]).isRequired,
  name: PropTypes.string,
  dates: PropTypes.arrayOf(PropTypes.shape({})),
  code: PropTypes.string,
  eventFormat: PropTypes.string,
  eventType: PropTypes.string,
  credit: PropTypes.shape({}),
  isReserved: PropTypes.bool,
  primaryLanguage: PropTypes.string,
  secondaryLanguage: PropTypes.string,
  secondaryLanguageEnabled: PropTypes.bool,
  setName: PropTypes.func.isRequired,
  setDates: PropTypes.func.isRequired,
  setCode: PropTypes.func.isRequired,
  setEventFormat: PropTypes.func.isRequired,
  setEventType: PropTypes.func.isRequired,
  setPrimaryLanguage: PropTypes.func.isRequired,
  setSecondaryLanguage: PropTypes.func.isRequired,
  setSecondaryLanguageEnabled: PropTypes.func.isRequired,
};

export default Details;
